import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { InventoryService, LoggerService } from '../../services';
import { TranslateService } from '@ngx-translate/core';
import { WorkshopService } from '../../services/workshop.service';
import { AppSettings } from '../../services/app.settings';
import { S3BucketFoldersKeys } from '../../core/enums/S3BucketFoldersKeys';
import { ReplaySubject, Subject, forkJoin, of } from 'rxjs';
import { take, takeUntil, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-add-part',
  templateUrl: './add-part.component.html'
})
export class AddPartComponent implements OnInit, OnDestroy {
  form: FormGroup;
  variantForm: FormGroup;

  parentParts: any[] = [];
  workshops: any[] = [];
  allVariants: any[] = [];
  allPartVariants: any[] = [];
  filteredVariants: any[] = [];
  addedVariants: any[] = [];
  editingPartId: number = null;
  showVariantForm: boolean = false;
  isLoading: boolean = true;
  isSaving: boolean = false;

  isCloning: boolean = false;
  isEditing: boolean = false;

  currentLang: string;
  translationPlaceholderKey = 'INVENTORY.NORWEGIAN_TRANSLATION';

  imageFileName: string;
  localPreviewUrl: SafeUrl;
  private objectUrl: string;

  variantImageFileName: string;
  variantImagePreviewUrl: SafeUrl;
  private variantObjectUrl: string;

  /** Workshop Search related */
  public filteredWorkshopsMulti: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  private currentFilteredWorkshops: any[] = [];
  protected _onDestroy = new Subject<void>();

  public checkAllWorkshops: boolean = false;

  public allSelected: boolean = false;
  public indeterminate: boolean = false;

  constructor(
    private fb: FormBuilder,
    private inventoryService: InventoryService,
    private loggerService: LoggerService,
    private translate: TranslateService,
    private workshopService: WorkshopService,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<AddPartComponent>,
    private appSettings: AppSettings,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang || 'en';
    this.updateTranslationPlaceholder();

    this.translate.onLangChange.subscribe(() => {
      this.currentLang = this.translate.currentLang || 'en';
      this.updateTranslationPlaceholder();
    });

    this.initializeForms();
    this.loadData();

    this.setupFormSubscriptions();

    this.form.get('WorkshopMultiFilter').valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterWorkshopsMulti();
      });

    this.form.get('Name').valueChanges.subscribe(val => {
    });

    // Monitor addedVariants to disable/enable Price field
    this.checkPriceFieldStatus();

    if (this.data && (this.data.PartId || this.data.partId)) {
      this.isEditing = true;
      this.editingPartId = this.data.PartId || this.data.partId;
      this.populateEditForm(this.data);
      this.loadExistingVariants(this.editingPartId, false);
      // Remove validators for fields that are hidden in edit mode
      const descControl = this.form.get('Description');
      const priceControl = this.form.get('Price');
      const quantityControl = this.form.get('Quantity');
      const workshopControl = this.form.get('WorkshopMulti');

      if (descControl) {
        descControl.clearValidators();
        descControl.updateValueAndValidity();
      }
      if (priceControl) {
        priceControl.clearValidators();
        priceControl.updateValueAndValidity();
      }
      if (quantityControl) {
        quantityControl.clearValidators();
        quantityControl.updateValueAndValidity();
      }
      if (workshopControl) {
        workshopControl.clearValidators();
        workshopControl.updateValueAndValidity();
      }
    } else {
      this.isCloning = false;
      this.isEditing = false;
    }
  }

  private loadExistingVariants(partId: number, asClone: boolean = false): void {
    console.log('🔍 loadExistingVariants called - PartId:', partId, 'asClone:', asClone);
    this.inventoryService.getAllVariantsforPart(partId).subscribe(variants => {
      console.log('🔍 API returned', (variants || []).length, 'variants for part', partId);
      if (variants && variants.length > 0) {
        console.log('🔍 First variant:', JSON.stringify(variants[0]));
      }

      if (asClone) {
        // When cloning, only get the variant names, don't copy other details
        // User requirement: "when adding clone item variant just get the variant name only other details are not necessary"
        this.addedVariants = [];
        // Extract unique variant names for filtering purposes
        const uniqueVariantIds = new Set<number>();
        (variants || []).forEach(v => {
          if (v.VariantId && !uniqueVariantIds.has(v.VariantId)) {
            uniqueVariantIds.add(v.VariantId);
          }
        });
        // The user can now manually add variants with just their names via the variant form
        console.log('Clone mode: Loaded variant names for filtering, not copying details');
      } else {
        // When editing, load all existing variant details
        this.addedVariants = (variants || []).map(v => {
          let imageUrl = v.VariantImageUrl || v.ImageUrl;
          if (imageUrl && !imageUrl.startsWith('http')) {
            imageUrl = this.appSettings.s3_Image_URL + S3BucketFoldersKeys.Parts + '/' + imageUrl;
          }
          return {
            Id: v.Id,
            PartId: partId,
            VariantId: v.VariantId,
            VariantName: v.VariantName || v.Name || 'Standard',
            Quantity: v.Quantity,
            Cost: v.Cost,
            WorkshopId: v.WorkshopId,
            WorkshopName: v.WorkshopName,
            Active: v.Active,
            VariantImageUrl: imageUrl,
            IsExisting: true
          };
        });
        console.log('🔍 Edit mode: Populated addedVariants with', this.addedVariants.length, 'variants');
      }
      this.checkPriceFieldStatus();
    });
  }

  private populateEditForm(part: any): void {
    const partName = part.PartName || part.Name;
    const translationKey = this.getInventoryPartMappingName(partName);

    this.form.patchValue({
      Name: partName,
      TranslationName: part.PartTranslationName || part.TranslationName || null,
      Description: part.PartDescription || part.Description,
      Price: part.Price || 0,
      ParentId: part.ParentId,
    });

    if (part.PartImageUrl) {
      this.localPreviewUrl = this.appSettings.s3_Image_URL + S3BucketFoldersKeys.Parts + '/' + part.PartImageUrl;
      this.imageFileName = part.PartImageUrl;
    }
    this.checkPriceFieldStatus();
  }

  get hasRealVariants(): boolean {
    return this.addedVariants && this.addedVariants.some(v => v.VariantId > 0);
  }

  get groupedAddedVariants(): any[] {
    const groups = new Map<string, any>();
    this.addedVariants.forEach((v, index) => {
      const key = this.getVariantKey(v);
      if (!groups.has(key)) {
        groups.set(key, {
          ...v,
          GroupKey: key,
          Workshops: [v.WorkshopName],
          OriginalIndices: [index],
          ParentName: this.form.get('Name').value,
          ParentPrice: this.form.get('Price').value,
          ShowParentInfo: v.ShowParentInfo
        });
      } else {
        const group = groups.get(key);
        group.Workshops.push(v.WorkshopName);
        group.Quantity += v.Quantity;
        group.OriginalIndices.push(index);
        if (v.ShowParentInfo) group.ShowParentInfo = true;
      }
    });
    return Array.from(groups.values());
  }

  private checkPriceFieldStatus(): void {
    const priceControl = this.form.get('Price');
    if (this.hasRealVariants) {
      if (priceControl.enabled) priceControl.disable();
    } else {
      if (priceControl.disabled) priceControl.enable();
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
    if (this.variantObjectUrl) {
      URL.revokeObjectURL(this.variantObjectUrl);
    }
  }

  private initializeForms(): void {
    this.form = this.fb.group({
      ClonePartId: [null], // Default to "Create new part" option
      Name: [null, Validators.required],
      ParentId: [0],
      TranslationName: [null, Validators.required],
      Description: [null],
      Price: [0],
      Cost: [0],
      VariantId: [0],
      Quantity: [0],
      WorkshopMulti: [[]],
      WorkshopMultiFilter: [''],
      Active: [true]
    });

    this.variantForm = this.fb.group({
      VariantId: [null],
      VariantName: [null],
      Cost: [null],
      Active: [false]
    }, { validators: this.variantValidator.bind(this) });
  }

  private loadData(): void {
    this.isLoading = true;

    forkJoin([
      this.inventoryService.getAllPartDetails().pipe(catchError(err => { console.error('parts details error', err); return of([]); })),
      this.inventoryService.getAllBikeVariants().pipe(catchError(err => { console.error('variants error', err); return of([]); })),
      this.inventoryService.getAllPartsAndVariants().pipe(catchError(err => { console.error('inventory error', err); return of([]); })),
      this.workshopService.getAllWorkshopsWithStreetTeam().pipe(catchError(err => { console.error('workshops error', err); return of([]); }))
    ]).pipe(takeUntil(this._onDestroy)).subscribe(([partDetails, variants, inventoryRelations, workshops]: [any, any, any, any]) => {
      // 1. Process Parts
      this.parentParts = (partDetails || []).sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));

      // 2. Process Variants
      this.allVariants = (variants || []).map(v => {
        // Transform image URL to full S3 path if it's just a filename
        if (v.ImageUrl && !v.ImageUrl.startsWith('http')) {
          v.ImageUrl = this.appSettings.s3_Image_URL + S3BucketFoldersKeys.Parts + '/' + v.ImageUrl;
        }
        if (v.VariantImageUrl && !v.VariantImageUrl.startsWith('http')) {
          v.VariantImageUrl = this.appSettings.s3_Image_URL + S3BucketFoldersKeys.Parts + '/' + v.VariantImageUrl;
        }
        return v;
      });
      this.filteredVariants = this.allVariants;

      // 3. Process Inventory Relations
      this.allPartVariants = inventoryRelations || [];

      // 4. Process Workshops
      this.workshops = (workshops || []).sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
      this.currentFilteredWorkshops = this.workshops.slice();
      this.filteredWorkshopsMulti.next(this.currentFilteredWorkshops);

      // Pre-select all workshops if this is a new part (satisfies "select them at once")
      if (!this.isEditing && !this.form.get('WorkshopMulti').value?.length) {
        this.form.get('WorkshopMulti').patchValue(this.workshops);
      }

      this.updateSelectAllState(); // Initial state update

      this.isLoading = false;
    }, (error) => {
      console.error('Failed to load add-part data', error);
      this.isLoading = false;
    });
  }

  private filterWorkshopsMulti(): void {
    if (!this.workshops) return;
    let search = this.form.get('WorkshopMultiFilter').value;
    if (!search) {
      this.currentFilteredWorkshops = this.workshops.slice();
    } else {
      search = search.toLowerCase();
      this.currentFilteredWorkshops = this.workshops.filter(workshop => workshop.Name.toLowerCase().indexOf(search) > -1);
    }
    this.filteredWorkshopsMulti.next(this.currentFilteredWorkshops);
    this.updateSelectAllState();
  }

  public onWorkshopSelectionChange(): void {
    this.updateSelectAllState();
    this.cdr.detectChanges();
  }

  public updateSelectAllState(): void {
    const selected = this.form.get('WorkshopMulti').value || [];
    if (!this.currentFilteredWorkshops || this.currentFilteredWorkshops.length === 0) {
      this.allSelected = false;
      this.indeterminate = false;
      return;
    }

    // Check how many visible workshops are selected
    const matchCount = this.currentFilteredWorkshops.filter(f => selected.some(s => s && f && s.Id == f.Id)).length;

    this.allSelected = this.currentFilteredWorkshops.length > 0 && matchCount === this.currentFilteredWorkshops.length;
    this.indeterminate = matchCount > 0 && !this.allSelected;
  }

  public toggleSelectAllWorkshops(selectAllValue: boolean): void {
    if (selectAllValue) {
      // Add all CURRENTLY FILTERED workshops to the selection
      const currentSelection = this.form.get('WorkshopMulti').value || [];
      const newSelection = [...currentSelection];

      this.currentFilteredWorkshops.forEach(w => {
        if (!newSelection.some(s => s.Id == w.Id)) {
          newSelection.push(w);
        }
      });

      this.form.get('WorkshopMulti').setValue(newSelection);
    } else {
      // Deselect all visible workshops
      const currentSelection = this.form.get('WorkshopMulti').value || [];
      const visibleIds = new Set(this.currentFilteredWorkshops.map(w => w.Id));
      const newSelection = currentSelection.filter(s => !visibleIds.has(s.Id));

      this.form.get('WorkshopMulti').setValue(newSelection);
    }
    this.form.get('WorkshopMulti').markAsDirty();
    this.updateSelectAllState();
    this.cdr.detectChanges();
  }

  public isAllWorkshopsSelected(): boolean {
    return this.allSelected;
  }

  public isSomeWorkshopsSelected(): boolean {
    return this.indeterminate;
  }

  public compareWorkshops(o1: any, o2: any): boolean {
    return o1 && o2 ? o1.Id === o2.Id : o1 === o2;
  }

  private setupFormSubscriptions(): void {
    this.form.get('ClonePartId').valueChanges
      .pipe(takeUntil(this._onDestroy)) // Ensure unsubscription
      // .pipe(distinctUntilChanged()) // Add if import available, otherwise manual check
      .subscribe(clonePartId => {
        this.onClonePartSelect(clonePartId);
      });

    this.form.get('WorkshopMulti').valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }



  private updateTranslationPlaceholder(): void {
    // User Requirement: Name is ALWAYS English, TranslationName is ALWAYS Norwegian
    this.translationPlaceholderKey = 'INVENTORY.NORWEGIAN_TRANSLATION';
  }

  save(): void {
    if (this.form.invalid) {
      this.loggerService.showWarningMessage('Please fill all required fields.');
      return;
    }

    this.isSaving = true;
    this.form.disable();

    if (this.isEditing) {
      this.updateExistingPart();
    } else {
      // Save the part with variants if any have been added, otherwise save part only
      if (this.addedVariants.length > 0) {
        this.createPartWithVariants();
      } else {
        this.createPartOnly();
      }
    }
  }

  private updateExistingPart(): void {
    const formValue = this.form.getRawValue();
    const payload = {
      PartId: this.editingPartId,
      Name: formValue.Name,
      Description: formValue.Description || null,
      Price: formValue.Price || 0,
      ImageUrl: this.imageFileName || null,
      ParentId: formValue.ParentId || 0,
      TranslationName: formValue.TranslationName ? formValue.TranslationName.trim() : null
    };

    console.log('→ Final Update Payload:', JSON.stringify(payload, null, 2));
    console.log('→ Price field enabled?', this.form.get('Price').enabled);
    console.log('→ Price field value:', this.form.get('Price').value);

    this.inventoryService.updatePart(payload).subscribe(
      (response) => {
        console.log('UpdatePart success response:', response);
        console.log('\u2192 AFTER UPDATE - Cost:', formValue.Cost, 'addedVariants:', this.addedVariants.length);
        if (this.addedVariants.length > 0) {
          console.log('\u2192 First variant:', JSON.stringify(this.addedVariants[0]));
        }

        // Handle Logic: "if there are variants then variant related price need to update"
        // And "change the cost as well"
        // And "if variant is available then variant image upload also show"

        if (this.addedVariants.length > 0) {
          // Propagate Price, Cost, and Variant Image to variants
          this.addedVariants.forEach(v => {
            // FIX: Only update variant price if parent price control is enabled (i.e., user could change it)
            if (this.form.get('Price').enabled) {
              v.Price = formValue.Price;
            }

            // FIX: If it's the base part (VariantId=0), its Cost should match the Part Cost (Price)
            // Otherwise, use the Variant Cost field
            if (v.VariantId === 0) {
              v.Cost = formValue.Price;
            } else {
              v.Cost = formValue.Cost;
            }

            if (this.variantImageFileName) {
              v.VariantImageUrl = this.variantImageFileName; // Update Variant Image if uploaded
            }
          });
          console.log('→ Calling saveVariants with', this.addedVariants.length, 'variants');
          this.saveVariants(this.editingPartId);
        } else {
          // No variants (only part) -> We still might need to update Cost for the part-variant (VariantId=0)
          // Since UpdatePart doesn't handle Cost, we iterate selected workshops to update specific cost?
          // But addedVariants is empty. This implies we might need to fetch current workshop context or use specific update logic.
          // For now, let's assume if no variants, we try to update the default variant (0) if feasible,
          // OR rely on seedSpecificWorkshops logic if adding new workshop relations.

          // However, for EDITING existing relation (Cost update), we need a method to update Cost.
          // The current code for 'else' block calls seedSpecificWorkshops ONLY if selectedWorkshops > 0
          // This creates NEW endpoints. It doesn't update existing Cost.
          // User requirement: "change the cost as well".

          // For now, implementing the Explicit requirement for Variants:
          // "if there are varient then varient related price need to update" -> Done above.
          // "if there is no varient only part then it should update part price" -> Done via payload.Price.

          const selectedWorkshops = this.form.get('WorkshopMulti').value || [];
          if (selectedWorkshops.length > 0) {
            this.seedSpecificWorkshops(this.editingPartId, 0, formValue.Quantity || 0, formValue.Price || 0, selectedWorkshops);
          } else {
            this.showSuccessAndClose();
          }
        }
      },
      () => {
        this.isSaving = false;
        this.form.enable();
        this.loggerService.showErrorMessage(this.translate.instant('INVENTORY.SAVE_FAILED'));
      }
    );
  }


  private createPartOnly(): void {
    const payload = this.buildPartPayload();
    const selectedWorkshops = this.form.get('WorkshopMulti').value || [];

    console.log('=== CREATE PART ONLY ===');
    console.log('Selected workshops:', selectedWorkshops.length, selectedWorkshops.map(w => w.Name));
    console.log('Payload WorkshopId:', payload.WorkshopId);
    console.log('Payload SeedAllWorkshops:', payload.SeedAllWorkshops);

    console.log('→ FINAL CREATE PAYLOAD:', JSON.stringify(payload, null, 2));
    this.inventoryService.createPart(payload).subscribe(
      (response: any) => {
        // API now returns { PartId, VariantId } instead of just partId
        const partId = response.PartId || response; // Support both old and new format
        const variantId = response.VariantId || 0;
        console.log('CreatePart success. PartId:', partId, 'VariantId:', variantId);

        // If "All Workshops" wasn't selected, but multiple specific ones were, 
        // we need to add the inventory entries for the remaining ones.
        if (!payload.SeedAllWorkshops && selectedWorkshops.length > 1) {
          const remainingWorkshops = selectedWorkshops.slice(1);
          this.isSaving = true; // Keep loading spinner active
          this.seedingInProgress = true;
          this.seedSpecificWorkshops(partId, variantId, payload.Quantity, payload.Price, remainingWorkshops);
        } else {
          this.showSuccessAndClose();
        }
      },
      () => {
        this.isSaving = false;
        this.form.enable();
        this.loggerService.showErrorMessage(this.translate.instant('INVENTORY.SAVE_FAILED'));
      }
    );
  }

  private seedingInProgress = false;

  private seedSpecificWorkshops(partId: number, variantId: number, quantity: number, cost: number, workshops: any[]): void {
    if (workshops.length === 0) {
      this.seedingInProgress = false;
      this.showSuccessAndClose();
      return;
    }

    const workshop = workshops[0];
    const payload = {
      PartId: partId,
      VariantId: variantId,
      WorkshopId: workshop.Id,
      Quantity: quantity,
      Cost: cost,
      Active: true
    };

    this.inventoryService.createPartVariant(payload).subscribe(
      () => {
        this.seedSpecificWorkshops(partId, variantId, quantity, cost, workshops.slice(1));
      },
      (error) => {
        this.loggerService.showErrorMessage(`Failed to seed workshop: ${workshop.Name}`);
        // Continue even if one fails
        this.seedSpecificWorkshops(partId, variantId, quantity, cost, workshops.slice(1));
      }
    );
  }

  private createPartWithVariants(): void {
    const payload = this.buildPartPayload();

    // Always create a new base part if not editing
    const partPayload = { ...payload, VariantId: 0, Quantity: 0 };
    this.inventoryService.createPart(partPayload).subscribe(
      response => {
        const partId = response?.PartId || response;
        if (partId && partId > 0) {
          this.saveVariants(partId);
        } else {
          this.isSaving = false;
          this.form.enable();
          this.loggerService.showErrorMessage(this.translate.instant('INVENTORY.SAVE_FAILED'));
        }
      },
      () => {
        this.isSaving = false;
        this.form.enable();
        this.loggerService.showErrorMessage(this.translate.instant('INVENTORY.SAVE_FAILED'));
      }
    );
  }

  private saveVariants(partId: number): void {
    const newVariants = this.addedVariants.filter(v => !v.IsExisting);
    const existingVariants = this.addedVariants.filter(v => v.IsExisting);

    // First, update existing variants
    let updatedCount = 0;
    if (existingVariants.length > 0) {
      existingVariants.forEach(v => {
        const updatePayload = {
          Id: v.Id,
          PartId: partId,
          VariantId: v.VariantId,
          WorkshopId: v.WorkshopId,
          VariantName: v.VariantName,
          Quantity: v.Quantity,
          Active: v.Active,
          Cost: v.Cost || 0,
          VariantImageUrl: this.variantImageFileName || v.VariantImageUrl || null
        };

        this.inventoryService.updatePartVariant(updatePayload).subscribe(
          () => {
            updatedCount++;
            if (updatedCount === existingVariants.length) {
              // After updating all existing, create new ones
              if (newVariants.length > 0) {
                this.processVariantsSequentially(partId, newVariants, 0);
              } else {
                this.showSuccessAndClose();
              }
            }
          },
          (err) => {
            console.error('✗ Failed to update variant:', err);
            updatedCount++;
            if (updatedCount === existingVariants.length) {
              if (newVariants.length > 0) {
                this.processVariantsSequentially(partId, newVariants, 0);
              } else {
                this.showSuccessAndClose();
              }
            }
          }
        );
      });
    } else if (newVariants.length > 0) {
      // No existing variants, just create new ones
      this.processVariantsSequentially(partId, newVariants, 0);
    } else {
      // No variants at all
      this.showSuccessAndClose();
    }
  }

  private processVariantsSequentially(partId: number, variants: any[], index: number): void {
    if (index >= variants.length) {
      this.showSuccessAndClose();
      return;
    }

    const variant = variants[index];
    const partVariant = {
      PartId: partId,
      VariantId: variant.VariantId || 0,
      VariantName: variant.VariantName,
      Quantity: variant.Quantity,
      WorkshopId: variant.WorkshopId,
      Active: variant.Active,
      Cost: variant.Cost || null,
      VariantImageUrl: this.variantImageFileName || variant.VariantImageUrl || null
    };

    this.inventoryService.createPartVariant(partVariant).subscribe(
      () => {
        this.processVariantsSequentially(partId, variants, index + 1);
      },
      () => {
        this.loggerService.showErrorMessage(`Error creating variant: ${variant.VariantName}`);
        this.processVariantsSequentially(partId, variants, index + 1);
      }
    );
  }

  private buildPartPayload(): any {
    const formValue = this.form.getRawValue();
    const variantFormValue = this.variantForm ? this.variantForm.getRawValue() : null;
    const hasVariants = this.addedVariants.length > 0;
    const selectedWorkshops = formValue.WorkshopMulti || [];

    // Get variant name from either the variant form OR from addedVariants
    let variantName = null;
    if (variantFormValue && variantFormValue.VariantName) {
      variantName = variantFormValue.VariantName.trim();
    } else if (hasVariants && this.addedVariants[0]) {
      variantName = this.addedVariants[0].VariantName;
    }

    return {
      Name: formValue.Name,
      ParentId: formValue.ParentId || 0,
      ImageUrl: this.imageFileName || null,
      TranslationName: formValue.TranslationName ? formValue.TranslationName.trim() : null,
      Description: formValue.Description || null,
      Price: formValue.Price || 0,
      Cost: 0,
      VariantId: hasVariants ? (formValue.VariantId || 0) : 0,
      VariantName: variantName,  // ADD: Variant name from form
      VariantDescription: null,  // ADD: Can be populated later if needed
      VariantImageUrl: this.variantImageFileName || null,  // ADD: Variant image
      Quantity: formValue.Quantity || 0,
      SeedAllWorkshops: this.checkAllWorkshops,
      WorkshopId: selectedWorkshops.length > 0 ? selectedWorkshops[0].Id : null,
      Active: formValue.Active !== undefined ? formValue.Active : true
    };
  }

  private getInventoryPartMappingName(name: string): string {
    if (!name) return 'INVENTORY.PARTS.UNKNOWN';
    return `INVENTORY.PARTS.${name.replace(/ /g, "_").toUpperCase()}`;
  }

  private showSuccessAndClose(): void {
    this.isSaving = false;
    this.form.enable();
    this.loggerService.showSuccessfulMessage(this.translate.instant('INVENTORY.PART_SAVED_SUCCESSFULLY'));
    this.dialogRef.close(true);
  }

  onClonePartSelect(clonePartId: number | null): void {
    console.log('onClonePartSelect triggered with:', clonePartId);
    if (!clonePartId || clonePartId === 0) {
      this.isCloning = false;
      this.form.patchValue({
        ParentId: 0
      }, { emitEvent: false });
      this.form.get('TranslationName').clearValidators();
      this.form.get('TranslationName').updateValueAndValidity({ emitEvent: false });
      this.filteredVariants = this.allVariants;

      // CRITICAL FIX: Don't clear addedVariants when in edit mode!
      // But ONLY preserve REAL variants (VariantId > 0), not the default "Standard" variant
      if (!this.isEditing) {
        this.addedVariants = []; // Only clear when creating a new part
      } else {
        // In edit mode, keep only REAL variants, filter out default (VariantId=0)
        this.addedVariants = this.addedVariants.filter(v => v.VariantId > 0);
      }
      return;
    }

    this.isCloning = true;
    const selectedPart = this.parentParts.find(p => p.PartId === clonePartId);
    if (selectedPart) {
      this.form.patchValue({
        ParentId: clonePartId
      }, { emitEvent: false });
      // Dependency on source part names and description removed per user request
      this.form.get('TranslationName').setValidators([Validators.required]);
      this.form.get('TranslationName').updateValueAndValidity();
      this.filterVariantsByPart(clonePartId);
    }
  }

  private filterVariantsByPart(partId?: number): void {
    const addedVariantIds = new Set(this.addedVariants.map(av => av.VariantId).filter(id => id));

    // If no part selected, show all variants
    if (!partId || partId === 0) {
      this.filteredVariants = this.allVariants.filter(v => !addedVariantIds.has(v.VariantId));
      return;
    }

    // 1. Find the parent ID of the current part to also show sibling variants
    const currentPart = this.parentParts.find(p => p.PartId === partId);
    const parentId = currentPart?.ParentId || 0;

    // 2. Find all VariantIds associated with this PartId OR its parent from the inventory relations
    const relevantVariantIds = new Set<number>();

    // Add variants that are directly assigned to this part or its parent
    this.allPartVariants.forEach(pv => {
      if (pv.PartId === partId || (parentId > 0 && pv.PartId === parentId)) {
        if (pv.VariantId) relevantVariantIds.add(pv.VariantId);
      }
    });

    // 3. Perform the actual filter in code
    const partNameLower = (currentPart?.Name || '').toLowerCase();

    this.filteredVariants = this.allVariants.filter(v => {
      // Skip if already added to the list
      if (addedVariantIds.has(v.VariantId)) return false;

      // Match if the variant is in the relevant list from database relations
      if (relevantVariantIds.has(v.VariantId)) {
        return true;
      }

      // FALLBACK: Name matching if relations are sparse (e.g., variant name contains part name)
      // This helps if the part is "Fork" and the variant is "Fork crown cover"
      if (partNameLower && v.Name?.toLowerCase().includes(partNameLower)) {
        return true;
      }

      return false;
    });
  }

  variantValidator(formGroup: FormGroup): any {
    const variantId = formGroup.get('VariantId').value;
    const variantName = formGroup.get('VariantName').value;

    // If no existing variant selected, we MUST have a name
    if ((!variantId || variantId === 0) && (!variantName || !variantName.trim())) {
      return { required: true };
    }
    return null;
  }

  toggleVariantForm(): void {
    this.showVariantForm = !this.showVariantForm;
    if (this.showVariantForm) {
      this.variantForm.reset({ Active: false, Quantity: 0, WorkshopId: this.workshops.length > 0 ? this.workshops[0].Id : null });
      // Filter variants by the current part being edited or cloned
      const partIdToFilter = this.editingPartId || this.form.get('ClonePartId').value;
      this.filterVariantsByPart(partIdToFilter);
      this.variantImageFileName = null;
      this.variantImagePreviewUrl = null;
      if (this.variantObjectUrl) {
        URL.revokeObjectURL(this.variantObjectUrl);
        this.variantObjectUrl = null;
      }
    }
  }

  onVariantSelectionChange(): void {
    const variantId = this.variantForm.get('VariantId').value;
    if (variantId) {
      this.variantForm.patchValue({ VariantName: null });
      const selectedVariant = this.filteredVariants.find(v => v.VariantId === variantId)
        || this.allVariants.find(v => v.VariantId === variantId);

      if (selectedVariant?.Cost && !this.variantForm.get('Cost').value) {
        this.variantForm.patchValue({ Cost: selectedVariant.Cost });
      }
    }
  }

  onVariantNameChange(): void {
    const variantName = this.variantForm.get('VariantName').value;
    if (variantName?.trim()) {
      this.variantForm.patchValue({ VariantId: null });
    }
  }

  addVariant(): void {
    if (this.variantForm.invalid) {
      this.loggerService.showWarningMessage('Invalid inputs. Please fill all required fields.');
      return;
    }

    const variantData = this.variantForm.getRawValue();
    console.log('[AddPart] addVariant raw data:', JSON.stringify(variantData));

    const totalQuantity = this.form.get('Quantity').value || 0;
    const selectedWorkshops = this.form.get('WorkshopMulti').value || [];

    if (totalQuantity < 0) {
      this.loggerService.showWarningMessage(this.translate.instant('INVENTORY.QUANTITY_REQUIRED_FOR_VARIANT'));
      return;
    }

    let { finalVariantName, finalVariantId, finalVariantImageUrl } = this.resolveVariantDetails(
      variantData.VariantId,
      variantData.VariantName
    );

    console.log(`[AddPart] Resolved details -> Name: ${finalVariantName}, ID: ${finalVariantId}`);

    // Use the uploaded image name if available, otherwise keep existing
    if (this.variantImageFileName) {
      finalVariantImageUrl = this.variantImageFileName;
    }

    // ... rest of method
    const targetWorkshopIds = selectedWorkshops.map(w => w.Id);

    console.log('Target workshops:', targetWorkshopIds.length);

    if (targetWorkshopIds.length === 0) {
      this.loggerService.showWarningMessage(this.translate.instant('INVENTORY.WORKSHOP_SELECTION_REQUIRED'));
      return;
    }

    const quantityPerWorkshop = totalQuantity;

    console.log('Total Quantity:', totalQuantity);
    console.log('Quantity per workshop:', quantityPerWorkshop);

    const workshopsAdded = this.addVariantToWorkshops(
      finalVariantName,
      finalVariantId,
      finalVariantImageUrl,
      variantData.Cost,
      quantityPerWorkshop,
      variantData.Active,
      targetWorkshopIds,
      true // ShowParentInfo
    );

    // ...
  }

  private resolveVariantDetails(variantId: any, variantName: string): { finalVariantName: string, finalVariantId: number | null, finalVariantImageUrl: string | null } {
    // 1. If an existing VariantId was selected from the dropdown
    if (variantId && variantId !== 0) {
      const selectedVariant = this.allVariants.find(v => v.VariantId == variantId);
      if (selectedVariant) {
        return {
          finalVariantName: selectedVariant.Name,
          finalVariantId: selectedVariant.VariantId,
          finalVariantImageUrl: selectedVariant.ImageUrl || selectedVariant.VariantImageUrl || null
        };
      }
    }

    // 2. If a NEW variant name was typed in
    if (variantName && variantName.trim()) {
      const trimmedName = variantName.trim();
      // Check if this typed name accidentally matches an existing global variant
      const existingVariant = this.allVariants.find(v => v.Name?.toLowerCase() === trimmedName.toLowerCase());

      return {
        finalVariantName: existingVariant?.Name || trimmedName,
        finalVariantId: existingVariant?.VariantId || null,
        finalVariantImageUrl: existingVariant?.ImageUrl || existingVariant?.VariantImageUrl || null
      };
    }

    // 3. Fallback to Standard
    return { finalVariantName: 'Standard', finalVariantId: null, finalVariantImageUrl: null };
  }

  private addVariantToWorkshops(
    variantName: string,
    variantId: number | null,
    variantImageUrl: string | null,
    cost: number,
    quantity: number,
    active: boolean,
    workshopIds: number[],
    showParentInfo: boolean = false
  ): number[] {
    const workshopsAdded: number[] = [];

    console.log('Initial addedVariants length:', this.addedVariants.length);
    workshopIds.forEach(workshopId => {
      const duplicate = this.addedVariants.find(av =>
        av.VariantId === variantId &&
        av.Cost === cost &&
        av.WorkshopId === workshopId
      );

      if (!duplicate) {
        console.log(`Adding variant to workshop ${workshopId}`);
        this.addedVariants.push({
          VariantId: variantId,
          VariantName: variantName,
          Quantity: quantity,
          Cost: cost,
          WorkshopId: workshopId,
          Active: active,
          WorkshopName: this.workshops.find(w => w.Id === workshopId)?.Name || '',
          IsExisting: false, // Ensure it's marked as new
          VariantImageUrl: variantImageUrl,
          VariantImagePreviewUrl: this.variantImagePreviewUrl,
          ShowParentInfo: showParentInfo
        });
        workshopsAdded.push(workshopId);
        // Trigger change detection to update UI
        this.cdr.detectChanges();
      } else {
        console.log(`Duplicate found for workshop ${workshopId}`);
      }
    });

    return workshopsAdded;
  }

  removeVariant(index: number): void {
    this.addedVariants.splice(index, 1);
    this.checkPriceFieldStatus();
    if (this.isCloning) {
      const selectedParentId = this.form.get('ClonePartId').value;
      if (selectedParentId) {
        this.filterVariantsByPart(selectedParentId);
      }
    }
  }

  removeGroupedVariant(group: any): void {
    const keyToRemove = group.GroupKey;
    this.addedVariants = this.addedVariants.filter(v => this.getVariantKey(v) !== keyToRemove);

    console.log('Remove Grouped Variant:', {
      keyToRemove,
      remainingCount: this.addedVariants.length
    });

    this.checkPriceFieldStatus();
    if (this.isCloning) {
      const selectedParentId = this.form.get('ClonePartId').value;
      if (selectedParentId) {
        this.filterVariantsByPart(selectedParentId);
      }
    }
  }

  private getVariantKey(v: any): string {
    return `${v.VariantName || ''}-${v.VariantId || 0}-${v.Cost || 0}-${v.VariantImageUrl || ''}`;
  }

  onImageSelected(event: any): void {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
    this.objectUrl = URL.createObjectURL(file);
    this.localPreviewUrl = this.sanitizer.bypassSecurityTrustUrl(this.objectUrl);

    // Upload to S3
    this.isSaving = true; // Show loading
    this.inventoryService.uploadImage(file, S3BucketFoldersKeys.Parts).subscribe(
      (response) => {
        console.log('Image uploaded successfully:', response.FileName);
        this.imageFileName = response.FileName;
        this.isSaving = false;
        this.loggerService.showSuccessfulMessage('Image uploaded');
      },
      (error) => {
        console.error('Image upload failed', error);
        this.loggerService.showErrorMessage('Image upload failed');
        this.isSaving = false;
      }
    );
  }

  private sanitizeFileName(original: string): string {
    const dot = original.lastIndexOf('.');
    const name = dot > -1 ? original.substring(0, dot) : original;
    const ext = dot > -1 ? original.substring(dot) : '';
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${slug}${ext.toLowerCase()}`;
  }

  onVariantImageSelected(event: any): void {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview
    if (this.variantObjectUrl) {
      URL.revokeObjectURL(this.variantObjectUrl);
    }
    this.variantObjectUrl = URL.createObjectURL(file);
    this.variantImagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(this.variantObjectUrl);

    // Upload to S3
    this.isSaving = true; // Show loading
    this.inventoryService.uploadImage(file, S3BucketFoldersKeys.Variants).subscribe(
      (response) => {
        console.log('Variant Image uploaded successfully:', response.FileName);
        this.variantImageFileName = response.FileName;
        this.isSaving = false;
        this.loggerService.showSuccessfulMessage('Variant Image uploaded');
      },
      (error) => {
        console.error('Variant Image upload failed', error);
        this.loggerService.showErrorMessage('Variant Image upload failed');
        this.isSaving = false;
      }
    );
  }

  private createStandardVariant(): void {
    const quantity = this.form.get('Quantity').value || 0;
    const selectedWorkshops = this.form.get('WorkshopMulti').value || [];
    const partName = this.form.get('Name').value;

    const targetWorkshopIds = selectedWorkshops.map(w => w.Id);

    if (targetWorkshopIds.length === 0) {
      this.loggerService.showWarningMessage(this.translate.instant('INVENTORY.WORKSHOP_SELECTION_REQUIRED'));
      return;
    }

    targetWorkshopIds.forEach(workshopId => {
      this.addedVariants.push({
        VariantId: null,
        VariantName: 'Standard',
        Quantity: quantity,
        Cost: 0,
        WorkshopId: workshopId,
        Active: true,
        WorkshopName: this.workshops.find(w => w.Id === workshopId)?.Name || '',
        IsNewVariant: true,
        VariantImageUrl: null,
        VariantImagePreviewUrl: null,
        ShowParentInfo: true
      });
    });
  }
}